import { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, query, where } from "firebase/firestore";
import { db } from '../../../firebase/firestore';
import { loadFromCache, saveToCache, updateCache, deleteFromCache } from '../utils/cache';

export const useUsers = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ type: '', message: '' });

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: '', message: '' }), 5000);
  };

  // Определяем тип пользователя по ID или данным
  const getUserType = (userId, userData) => {
    // Если есть поле email или phone - скорее всего веб-пользователь
    if (userData?.email && !userData?.username?.startsWith('@')) {
      return 'web';
    }
    // Если username начинается с @ или есть firstName - Telegram
    if (userData?.firstName || (userData?.username && userData.username.startsWith('@'))) {
      return 'telegram';
    }
    // По умолчанию проверяем по формату ID (Telegram ID - число, Firebase UID - строка с буквами)
    if (/^\d+$/.test(userId)) {
      return 'telegram';
    }
    return 'web';
  };

  const getUserTrainingsList = (user) => {
    const trainingsFromArray = user.trainings || [];
    const trainingsFromAccess = (user.trainingsAccess || []).map(access => access.id);
    return [...new Set([...trainingsFromArray, ...trainingsFromAccess])];
  };

  const getUserCoursesList = (user) => {
    const coursesFromArray = user.courses || [];
    const coursesFromAccess = (user.coursesAccess || []).map(access => access.id);
    return [...new Set([...coursesFromArray, ...coursesFromAccess])];
  };

  // Загрузка пользователей из обеих коллекций
  const fetchUsers = async (forceRefresh = false) => {
    setLoading(true);
    
    try {
      if (!forceRefresh) {
        const cachedUsers = loadFromCache();
        if (cachedUsers) {
          setUsers(cachedUsers);
          setFilteredUsers(cachedUsers);
          setLoading(false);
          return;
        }
      }
      
      const allUsers = [];
      
      // 1. Загружаем Telegram пользователей
      const telegramUsersRef = collection(db, "telegramUsers");
      const telegramSnapshot = await getDocs(telegramUsersRef);
      
      telegramSnapshot.docs.forEach(doc => {
        const data = doc.data();
        allUsers.push({
          id: doc.id,
          userType: 'telegram',
          username: data.username || null,
          fullName: data.fullName || data.firstName ? `${data.firstName || ''} ${data.lastName || ''}`.trim() : null,
          firstName: data.firstName || null,
          lastName: data.lastName || null,
          phone: data.phone || null,
          email: data.email || null,
          premium: data.premium === true,
          premiumExpiresAt: data.premiumExpiresAt || null,
          premiumActivatedAt: data.premiumActivatedAt || null,
          premiumActivationType: data.premiumActivationType || null,
          subscription: data.subscription || null,
          subscriptionPlan: data.subscription?.planName || null,
          subscriptionId: data.subscription?.subscriptionId || null,
          refBonus: data.refBonus || 0,
          birthDate: data.birthDate || null,
          savedAddress: data.savedAddress || null,
          joinedAt: data.joinedAt || new Date().toISOString(),
          referrerId: data.referrerId || null,
          lastActivity: data.lastActivity || null,
          trainings: data.trainings || [],
          courses: data.courses || [],
          trainingsAccess: data.trainingsAccess || [],
          coursesAccess: data.coursesAccess || []
        });
      });
      
      // 2. Загружаем Web пользователей
      const webUsersRef = collection(db, "webUsers");
      const webSnapshot = await getDocs(webUsersRef);
      
      webSnapshot.docs.forEach(doc => {
        const data = doc.data();
        allUsers.push({
          id: doc.id,
          userType: 'web',
          username: data.email || data.username || null,
          fullName: data.fullName || data.displayName || null,
          phone: data.phone || null,
          email: data.email || null,
          premium: data.premium === true,
          premiumExpiresAt: data.premiumExpiresAt || null,
          premiumActivatedAt: data.premiumActivatedAt || null,
          premiumActivationType: data.premiumActivationType || null,
          subscription: data.subscription || null,
          subscriptionPlan: data.subscription?.planName || null,
          subscriptionId: data.subscription?.subscriptionId || null,
          refBonus: data.refBonus || 0,
          birthDate: data.birthDate || null,
          savedAddress: data.savedAddress || null,
          joinedAt: data.joinedAt || new Date().toISOString(),
          referrerId: data.referrerId || null,
          lastActivity: data.lastActivity || null,
          trainings: data.trainings || [],
          courses: data.courses || [],
          trainingsAccess: data.trainingsAccess || [],
          coursesAccess: data.coursesAccess || []
        });
      });

      saveToCache(allUsers);
      setUsers(allUsers);
      setFilteredUsers(allUsers);
      
    } catch (error) {
      console.error("Ошибка загрузки пользователей:", error);
      showNotification('error', `Ошибка загрузки: ${error.message}`);
      
      const cachedUsers = loadFromCache();
      if (cachedUsers) {
        setUsers(cachedUsers);
        setFilteredUsers(cachedUsers);
        showNotification('warning', 'Используются кэшированные данные');
      }
    } finally {
      setLoading(false);
    }
  };

  // Обновление пользователя в правильной коллекции
  const updateUser = async (userId, updateData) => {
    try {
      // Определяем коллекцию по типу пользователя из текущего списка
      const user = users.find(u => u.id === userId);
      const collectionName = user?.userType === 'web' ? 'webUsers' : 'telegramUsers';
      
      const userDoc = doc(db, collectionName, userId);
      await updateDoc(userDoc, updateData);
      
      updateCache(userId, updateData);
      
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...updateData } : u));
      setFilteredUsers(prev => prev.map(u => u.id === userId ? { ...u, ...updateData } : u));
      
      showNotification('success', 'Пользователь обновлен');
      return true;
    } catch (error) {
      console.error("Ошибка обновления:", error);
      showNotification('error', `Ошибка: ${error.message}`);
      return false;
    }
  };

  // Удаление пользователя из правильной коллекции
  const deleteUser = async (userId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этого пользователя?')) {
      return false;
    }

    try {
      const user = users.find(u => u.id === userId);
      const collectionName = user?.userType === 'web' ? 'webUsers' : 'telegramUsers';
      
      await deleteDoc(doc(db, collectionName, userId));
      deleteFromCache(userId);
      
      setUsers(prev => prev.filter(u => u.id !== userId));
      setFilteredUsers(prev => prev.filter(u => u.id !== userId));
      
      showNotification('success', 'Пользователь удален');
      return true;
    } catch (error) {
      console.error("Ошибка удаления:", error);
      showNotification('error', `Ошибка: ${error.message}`);
      return false;
    }
  };

  const applyFilters = (userList, filter, trainingId = null, courseId = null) => {
    let filtered = [...userList];
    
    switch (filter) {
      case 'premium':
        filtered = userList.filter(user => user.premium === true);
        break;
      case 'free':
        filtered = userList.filter(user => user.premium !== true);
        break;
      case 'manual':
        filtered = userList.filter(user => 
          user.premium === true && user.premiumActivationType === 'manual'
        );
        break;
      case 'auto':
        filtered = userList.filter(user => 
          user.premium === true && user.premiumActivationType === 'auto'
        );
        break;
      case 'telegram':
        filtered = userList.filter(user => user.userType === 'telegram');
        break;
      case 'web':
        filtered = userList.filter(user => user.userType === 'web');
        break;
      case 'withTrainings':
        filtered = userList.filter(user => getUserTrainingsList(user).length > 0);
        break;
      case 'withCourses':
        filtered = userList.filter(user => getUserCoursesList(user).length > 0);
        break;
      default:
        filtered = userList;
    }
    
    if (trainingId) {
      filtered = filtered.filter(user => getUserTrainingsList(user).includes(trainingId));
    }
    
    if (courseId) {
      filtered = filtered.filter(user => getUserCoursesList(user).includes(courseId));
    }
    
    setFilteredUsers(filtered);
  };

  return {
    users,
    filteredUsers,
    setFilteredUsers,
    loading,
    notification,
    fetchUsers,
    updateUser,
    deleteUser,
    applyFilters,
    getUserTrainingsList,
    getUserCoursesList,
    showNotification
  };
};